import { TenantPartnersSettings } from "@/types/payments";
import { LocalStorageKeys } from "../../login/hooks/useLocalStorage";
import { tenantAdminService } from "@/services/tenant-admin-service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { is404 } from "@/utils/httpErrors";

export const tenantPartnersSettingsKeys = {
  all: ["tenantPartnersSettings"] as const,

  partnersSettings: () =>
    [...tenantPartnersSettingsKeys.all, "partnersSettings"] as const,
};

export const useGetTenantPartnersSettings = (enabled: boolean = true) => {
  const tenantId = localStorage.getItem(LocalStorageKeys.TENANT_ID);

  return useQuery<TenantPartnersSettings | null>({
    queryKey: tenantPartnersSettingsKeys.partnersSettings(),
    enabled: enabled && !!tenantId,
    queryFn: async () => {
      try {
        return await tenantAdminService.getTenantPartnersSettings(tenantId!);
      } catch (e) {
        if (is404(e)) return null;

        throw e;
      }
    },

    retry: (failureCount, error) => !is404(error) && failureCount < 2,
  });
};

export const useUpdateTenantPartnersSettings = () => {
  const tenantId = localStorage.getItem(LocalStorageKeys.TENANT_ID);
  const qc = useQueryClient();

  return useMutation<void, unknown, { enabled: boolean }>({
    mutationFn: async (payload) => {
      if (!tenantId) throw new Error("tenantId is required");
      return tenantAdminService.updateTenantPartnersSettings(tenantId, payload);
    },
    onSuccess: () => {
      if (!tenantId) return;
      qc.invalidateQueries({
        queryKey: tenantPartnersSettingsKeys.partnersSettings(),
      });
    },
  });
};
